import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useStore } from "../context/StoreContext";
import { formatEUR } from "../lib/api";
import Logo from "./Logo";

const links = [
  { to: "/boutique", label: "Boutique" },
  { to: "/livraison", label: "Livraison" },
  { to: "/retours", label: "Retours" },
  { to: "/contact", label: "Contact" },
];

const Navbar = () => {
  const { count, setDrawerOpen, settings } = useStore();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const threshold = settings?.shipping?.free_threshold
    ? (Number.isInteger(settings.shipping.free_threshold)
        ? `${settings.shipping.free_threshold} €`
        : formatEUR(settings.shipping.free_threshold))
    : "50 €";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMenuOpen(false), [location.pathname]);

  return (
    <header className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${scrolled ? "bg-ink/85 backdrop-blur-xl border-b border-line" : "bg-transparent border-b border-transparent"}`}>
      <div data-testid="top-banner" className="bg-gold text-ink">
        <p className="text-center font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.25em] py-2 px-4 font-medium" data-testid="top-banner-text">
          Livraison offerte dès {threshold} d'achat
        </p>
      </div>
      <div className="flex items-center justify-between px-4 sm:px-8 lg:px-16 h-16 sm:h-20">
        <Logo />

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `font-mono text-xs uppercase tracking-[0.22em] transition-colors duration-300 hover:text-white ${isActive ? "text-white" : "text-neutral-400"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="cart-open-button"
            onClick={() => setDrawerOpen(true)}
            className="relative group border border-line hover:border-white transition-colors duration-300 px-4 sm:px-5 h-10 flex items-center gap-2"
            aria-label="Ouvrir le panier"
          >
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-white">Panier</span>
            <span data-testid="cart-count-badge" className="min-w-5 h-5 px-1 flex items-center justify-center bg-white text-ink font-mono text-[11px] font-bold">
              {count}
            </span>
          </button>
          <button
            data-testid="mobile-menu-button"
            className="md:hidden w-10 h-10 flex flex-col items-center justify-center gap-1.5 border border-line"
            onClick={() => setMenuOpen((o) => !o)}
            aria-label="Menu"
          >
            <span className={`block w-5 h-px bg-white transition-transform duration-300 ${menuOpen ? "rotate-45 translate-y-[3.5px]" : ""}`} />
            <span className={`block w-5 h-px bg-white transition-transform duration-300 ${menuOpen ? "-rotate-45 -translate-y-[3.5px]" : ""}`} />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-ink/95 backdrop-blur-xl border-b border-line"
          >
            <div className="flex flex-col px-6 py-4">
              {links.map((l) => (
                <Link key={l.to} to={l.to} className="py-3 font-display font-bold uppercase tracking-widest text-white border-b border-line last:border-0">
                  {l.label}
                </Link>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
